import { useCallback, useMemo } from 'react';

import useHiddenCatalogue from './useHiddenCatalogue';
import useMyMenu from './useMyMenu';

/**
 * May the signed-in user see a given screen, card or tab — and may they CHANGE it?
 *
 * Two orthogonal questions, both answered here by catalogue code:
 *
 *   canSeeCode  VISIBILITY — is the capability on for the platform, and not un-ticked
 *               for this role? Combines the two subtractive gates via useHiddenCatalogue
 *               (GET /catalogue/visibility + denied_pages from GET /catalogue/my-menu).
 *
 *   readOnly    ACCESS LEVEL — does the user hold this feature/page at Full-Read rather
 *               than Full? Reads `read_only_codes` from /catalogue/my-menu, which the
 *               backend resolves with inheritance (a page under a read-only feature) and
 *               the multi-role "most permissive wins" rule already applied, so this is a
 *               plain membership test. A code the user can write (Full, or a legacy grant
 *               with no level) is simply absent from the list.
 *
 * WHY THIS IS NOT `granted_codes`. See the note that used to live here: absence of a grant
 * row means "inherits", not "denied", so visibility is driven by denied_pages, never by
 * subtracting grants. read_only_codes is a separate, positively-stated list — a code is on
 * it only when an administrator deliberately set the level to read-only — so folding it in
 * can only DISABLE a write control, never hide a screen or widen access.
 *
 * Kept as its own hook because a card or a CTA asks about a CODE: `canSeeCode('add_student')`
 * / `readOnly('child_profiles')` read better at the call site than a negated filter, and it
 * fails open on a not-yet-loaded menu in one place rather than at every caller.
 */
export default function usePageAccess() {
  const filter = useHiddenCatalogue();
  const myMenu = useMyMenu();

  /** By catalogue page (or feature) code — the precise visibility check. */
  const canSeeCode = useCallback((code) => !code || !filter || !filter.codeHidden(code), [filter]);

  /**
   * By URL. Prefer canSeeCode wherever the code is known: a URL does not identify a
   * catalogue code on its own, and several roles reach the same screen by different paths.
   */
  const canSeeUrl = useCallback((url) => !filter || !filter.urlHidden(url), [filter]);

  /**
   * Is this feature/page held at a READ-ONLY level (Full-Read)? Fails OPEN — a code is
   * treated as writable until the menu has loaded and while it is absent from the list —
   * so a slow request or a Full grant never disables a control that should work. The
   * backend refusing the write (403) is the real guarantee; this is the UI reflecting it.
   */
  const readOnly = useCallback(
    (code) => Boolean(code) && Array.isArray(myMenu?.read_only_codes) && myMenu.read_only_codes.includes(code),
    [myMenu]
  );

  /** May the user change this feature/page? The inverse of readOnly. */
  const canWrite = useCallback((code) => !readOnly(code), [readOnly]);

  return useMemo(
    () => ({ canSeeCode, canSeeUrl, readOnly, canWrite }),
    [canSeeCode, canSeeUrl, readOnly, canWrite]
  );
}
